import React, { useEffect, useState } from "react"
import { Table, Layout, Empty, Row, Col } from "antd"
import type { ColumnsType } from "antd/es/table"
import { Notification } from "@presentation/components/common"
import { Pagination, Filter, type OptionType, Button } from "@presentation/components/form"

import style from "./style"
import type { TableRowSelection } from "antd/es/table/interface"
import FiltersBase from "@/@presentation/modules/schedule/pages/home/FiltersBase"
import { useItemsContext } from "@/@presentation/contexts/schedulesContext"

export interface DataType {
  key: React.Key
  name: string
  id: number
}

export interface ILoadData {
  offset: number
  limit: number
  selectedKey: string | undefined
  value: string | undefined
  dtIni?: string
  dtFim?: string
  search?: string
  schedule_id?: number
  status?: string
  patient?: string
  requester?: string
  surgeon_ids?: string
  unity_ids?: string
  statusBilling?: string
}

interface IList {
  limitPerPage?: number
  columns: ColumnsType<DataType | any>
  loadData?: (props: ILoadData) => Promise<any>
  options?: OptionType
  Form?: (props: any) => JSX.Element
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  loading: boolean
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  modalVisible: boolean
  setParams: React.Dispatch<any>
  params: any
  isReload: boolean
  setReload: React.Dispatch<boolean>
  headerChildren?: React.ReactElement
  isAdd?: boolean
  onAdd?: () => void | undefined
  rowSelection?: TableRowSelection<any> | undefined
  onRow?: (record: any, rowIndex: any) => void
  hideDataFilter?: boolean
  showStatusFilter?: boolean
  showStatusBillingFilter?: boolean
  selectedKeyView?: any
  showPagination?: boolean
  rowKey?: string
  reload?: boolean
  data?: any[]
}

const BaseList = (props: IList) => {
  const { showPagination = true } = props
  const {
    initialDate,
    finalDate,
    selectedKey,
    setSelectedKey,
    statusFilter,
    scheduleId,
    locale,
    requester,
    surgeon,
    patient,
    createdInitial,
    createdFinal,
    statusBilling,
  } = useItemsContext()

  const { currentPagination, setCurrentPagination } = useItemsContext()

  const [dataRaw, setDataRaw] = useState<any>([])
  const [data, setData] = useState<any>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(props.limitPerPage ? props.limitPerPage : 20)
  const [total, setTotal] = useState(0)
  const [value, setValue] = useState<string>("")

  const [filter, setFilter] = useState("")

  async function loadData() {
    if (!props?.loadData || props.loading) return
    try {
      props.setLoading(true)
      var schd_id
      if (!isNaN(filter)) {
        schd_id = Number.parseInt(filter)
        //setFilter("")
      }

      const params: any = {
        offset: currentPage !== 0 ? (currentPage - 1) * pageSize : 0,
        limit: pageSize,
        selectedKey,
        value,
        dtIni: initialDate,
        dtFim: finalDate,
      }

      if (statusFilter !== "" && statusFilter !== undefined) {
        params.status = statusFilter
      }

      if (locale !== "" && locale !== undefined) {
        params.unity_ids = locale
      }

      if (scheduleId !== "" && scheduleId !== undefined) {
        params.schedule_id = scheduleId
      }

      if (requester !== "" && requester !== undefined) {
        params.requester = requester
      }

      if (surgeon !== "" && surgeon !== undefined) {
        params.surgeon_ids = surgeon
      }

      if (patient !== "" && patient !== undefined) {
        params.patient = patient
      }

      if (!isNaN(schd_id)) {
        params.schedule_id = schd_id
      }

      if (filter !== "") {
        params.search = filter
        params.value = filter
      }

      if (selectedKey == "search") {
        params.search = ""
      }
      if (createdInitial !== "" && createdInitial !== undefined) {
        params.createdAt = createdInitial
      }

      if (createdFinal !== "" && createdFinal !== undefined) {
        params.endCreatedAt = createdFinal
      }

      if (statusBilling !== "" && statusBilling !== undefined) {
        params.statusBilling = statusBilling
      }

      const response = await props?.loadData(props.limitPerPage ? props.params : params)

      if (!showPagination) {
        setTotal(0)
        setData(response.data)
        setDataRaw(response.data)
        return
      }

      if (response && showPagination) {
        setTotal(response.pagination.total)
        setData(response.data)
        setDataRaw(response.data)
      } else {
        setTotal(0)
        setData([])
        setDataRaw([])
      }
    } catch (error: any) {
      Notification.error({
        message: error.message,
      })
    } finally {
      props.setLoading(false)
      props.setReload(false)
    }
  }

  const handleCancel = () => {
    if (props.loading) return
    props.setModalVisible(false)
    props.setParams(undefined)
  }

  const handleSave = async () => {
    if (props.loading) return
    props.setModalVisible(false)
    Notification.success({
      message: "Salvo com sucesso!",
      duration: 2,
    })

    await loadData()
  }

  useEffect(() => {
    loadData()

    if (currentPagination > 0) {
      setCurrentPage(currentPagination)
    }
  }, [currentPage, value, pageSize, props.isReload == true])

  const onShowSizeChange = (current: number, size: number) => {
    if (props.loading) return
    setCurrentPage(current)
    setCurrentPagination(current)
    setPageSize(size)
    props.setParams({ ...props.params, limit: size })
  }

  const onChange = (page: number, pageSize: number) => {
    if (props.loading) return
    setCurrentPage(page)
    setCurrentPagination(page)
    setPageSize(pageSize)
    props.setParams({ ...props.params, offset: (page - 1) * pageSize })
  }

  const handleFilterChange = async (key: string, value: string) => {
    if (props.loading) return
    setSelectedKey(key)
    setValue(value)
    setCurrentPage(0)
    setCurrentPagination(0)
    setPageSize(5)
  }

  useEffect(() => {
    loadData()
  }, [filter, props.params])

  useEffect(() => {
    setData(props.data)
  }, [props.data])

  // Função para garantir que as colunas tenham largura mínima baseada no título
  const processColumns = () => {
    if (!props.columns) return props.columns
  
    return props.columns.map((col) => ({
      ...col,
      ellipsis: true,
      onHeaderCell: col.onHeaderCell ?? (() => ({ style: { whiteSpace: "nowrap" } })),
      onCell: (record: any, rowIndex: number) => {
        const baseCell = col.onCell?.(record, rowIndex) ?? {}
        return {
          ...baseCell,
          style: { ...(baseCell.style || {}), whiteSpace: "nowrap" },
        }
      },
    }))
  }
  

  // Colunas processadas
  const processedColumns = processColumns()

  return (
    <Layout
      style={{
        backgroundColor: "white",
        minWidth: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {props?.headerChildren}

      {props.hideDataFilter === true ? (
        <></>
      ) : (
        <FiltersBase
          setFilter={setFilter}
          dataRaw={dataRaw}
          setReload={props.setReload}
          showStatus={props.showStatusFilter ?? false}
          showStatusBilling={props.showStatusBillingFilter ?? false}
        />
      )}

      <Row>
        <Col xl={12} xxl={12} xs={24} span={24}></Col>
        <Col xl={12} xxl={12} xs={24} span={24} style={{ textAlign: "right" }}>
          {props?.options && <Filter options={props.options} onFilterChange={handleFilterChange} />}
        </Col>
      </Row>

      <Row>
        <Col xl={12} xxl={12} xs={24} span={24}></Col>
        <Col xl={12} xxl={12} xs={24} span={24} style={{ textAlign: "right" }}>
          {props?.isAdd && props?.onAdd && (
            <Button
              text="Adicionar"
              nativeProps={{
                "data-testid": "btn-add",
                type: "primary",
                size: "middle",
                style: { margin: 10, marginTop: 20 },
              }}
              type="primary"
              data-testid="btn-add"
              size="middle"
              onClick={props.onAdd}
            />
          )}
        </Col>
      </Row>

      {props.selectedKeyView && (
        <Row>
          {/* The JSX variable is undeclared. Please fix the import or declare the variable before using it. */}
          <props.selectedKeyView />
        </Row>
      )}

      <Layout style={style.containerTable}>
        <div
          style={{
            width: "100%",
            overflow: "auto",
          }}
        >
          <Table
            tableLayout="auto"
            style={{ minWidth: "100%" }}
            data-testid="table-form"
            rowSelection={props?.rowSelection}
            columns={processedColumns}
            dataSource={data}
            pagination={false}
            loading={props.loading}
            size={"small"}
            rowKey={props.rowKey ?? "id"}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('button, a, svg, [role="button"]')) {
                    return;
                  }
            
                  if (props.onRow) {
                    return props.onRow(record, rowIndex);
                  }
                },
                style: { cursor: "pointer" },
              };
            }}            
            locale={{
              emptyText: (
                <Empty
                  description={<span>Não há dados</span>}
                  style={{
                    minHeight: "100%",
                    minWidth: "100%",
                  }}
                />
              ),
            }}
            // scroll={{ x: '100%', y: '100%' }}
            scroll={{ x: "max-content", y: "45vh" }}
          />
        </div>

        {showPagination && (
          <Layout style={style.pagination}>
            <Pagination
              total={total}
              defaultPageSize={pageSize}
              defaultCurrent={1}
              onShowSizeChange={onShowSizeChange}
              onChange={onChange}
              loading={props.loading}
              limitPerPage={props?.limitPerPage}
            />
          </Layout>
        )}
      </Layout>
      {props.Form && (
        <props.Form
          params={props.params}
          visible={props.modalVisible}
          ocultarModal={handleCancel}
          callback={handleSave}
        />
      )}
    </Layout>
  )
}

export const List = React.memo(BaseList)
